import * as React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  TextField,
  Grid,
  Chip,
  Skeleton,
  Alert,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { toSlug } from "../util/courseCodes";
import { useIdToken } from "@sans-service-tier/sdk-react";

const LOGIN_PENDING_KEY = "sans:loginPending";

// helpers
function normalizeLabel(s: string) {
  return s.toLowerCase().replace(/[\s_-]+/g, "");
}
function familyOf(code: string) {
  const m = code.match(/^[A-Za-z]{3}/);
  return m ? m[0].toUpperCase() : "GEN";
}

type CourseCard = { label: string; value: string; family: string };

export default function CoursesPage() {
  const { token, loading: authLoading } = useIdToken();
  const isAuthed = !!token && !authLoading;

  const [courseCodes, setCourseCodes] = React.useState<string[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");

  // Single source of truth for "we're redirecting to login"
  const [suppressFetch, setSuppressFetch] = React.useState(() => sessionStorage.getItem(LOGIN_PENDING_KEY) === "1");

  // Single fetch function so we can reuse it
  const fetchCourses = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = localStorage.getItem("sans-auth-access") ?? "";
      const token = raw.replace(/^"+|"+$/g, "");
      const headers: HeadersInit = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;

      const resp = await fetch("/workbook/api/courses", {
        method: "GET",
        headers,
        credentials: "include",
        cache: "no-store",
      });

      if (!resp.ok) {
        // choose message based on auth state
        if (resp.status === 401 || resp.status === 403) {
          setCourseCodes([]);
          if (isAuthed) {
            // User is signed in (header shows Logout), but backend denied (e.g., aud mismatch, expired, etc.)
            setError("We couldn’t load your courses. Please try again or contact support.");
          } else {
            // Not signed in: keep the gentle prompt
            setError("Please sign in to see your courses.");
          }
          return;
        }
        throw new Error(`HTTP ${resp.status}`);
      }

      const list: string[] = await resp.json();
      setCourseCodes(list);
    } catch (e) {
      console.error("CoursesPage load failed:", e);
      // If we’re here and user is authed, reflect “server-side problem” instead of “sign in”
      setError(
        isAuthed ? "We couldn’t load your courses. Please try again later." : "Please sign in to see your courses.",
      );
      setCourseCodes([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  // Initial load (skip while suppressing)
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (suppressFetch) return;
      // If auth is still resolving, wait until it resolves so error text is correct
      if (authLoading) return; // wait for auth hook to settle
      await fetchCourses();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchCourses, suppressFetch, authLoading]);

  // React to auth events (instant UI updates) — skip while suppressing
  React.useEffect(() => {
    if (suppressFetch) return;
    const onAuth = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      if (detail.type === "login") {
        fetchCourses();
      }
    };
    window.addEventListener("sans:auth", onAuth as EventListener);
    return () => window.removeEventListener("sans:auth", onAuth as EventListener);
  }, [fetchCourses, suppressFetch]);

  // When we come back visible (e.g., after IdP), clear flags and refresh
  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        if (sessionStorage.getItem("sans:logoutPending") === "1") {
          sessionStorage.removeItem("sans:logoutPending");
        }
        if (sessionStorage.getItem(LOGIN_PENDING_KEY) === "1") {
          sessionStorage.removeItem(LOGIN_PENDING_KEY);
          setSuppressFetch(false);
          fetchCourses();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchCourses]);

  // While redirecting to login, don't render the normal list/skeletons
  if (suppressFetch) {
    return (
      <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 6, textAlign: "center" }}>
        <Typography variant="h6">Redirecting to sign in…</Typography>
      </Box>
    );
  }

  // map codes -> cards with slug links, then sort alpha
  const courses = React.useMemo<CourseCard[] | null>(() => {
    if (!courseCodes) return null;

    const items = courseCodes.map(code => {
      const slug = toSlug(code); // e.g. SEC588-I01-WORKBOOK -> sec588-i01
      return {
        label: code,
        value: `/${slug}`, // BrowserRouter basename="/workbook"
        family: familyOf(code),
      };
    });

    items.sort((a, b) => a.label.localeCompare(b.label, "en", { sensitivity: "base", numeric: true }));
    return items;
  }, [courseCodes]);

  // client-side filter
  const filtered = React.useMemo(() => {
    if (!courses) return null;
    const q = normalizeLabel(query);
    if (!q) return courses;
    return courses.filter(c => normalizeLabel(c.label).includes(q));
  }, [courses, query]);

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2, maxWidth: 1400, mx: "auto" }}>
      {/* Top bar: only show when we’re not in an error state */}
      {!error && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Your Courses
          </Typography>
          <Box sx={{ flex: 1 }} />
          <TextField
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search courses…"
            size="small"
            sx={{ width: { xs: "100%", sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Error banner:
          - If not authed: show the "Please sign in to see your courses..." info banner
          - If authed: show an error banner (no “sign in” prompt since the header already shows Logout) */}
      {error && (
        <Alert severity={isAuthed ? "error" : "info"} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading || !filtered ? (
        <Grid container spacing={2}>
          {Array.from({ length: 12 }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card variant="outlined">
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="rounded" height={20} sx={{ mt: 1, width: 120 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filtered.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
          {query ? (
            <Typography>No courses match “{query}”.</Typography>
          ) : (
            // If we’re authenticated, keep the message neutral (no “sign in”)
            <Typography>{isAuthed ? "No courses available." : "No courses available."}</Typography>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map(c => (
            <Grid key={c.label} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 2, "&:hover": { boxShadow: 3 } }}>
                <CardActionArea component="a" href={`/workbook${c.value}/`} sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {c.label}
                    </Typography>
                    <Chip size="small" label={c.family} sx={{ mt: 1 }} variant="outlined" />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
